import React from "react";
import * as Icons from "lucide-react";
import { ArrowRight, Check, X } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { PROBLEMS, WITHOUT, WITH } from "@/data/mock";

export const ProblemSection = () => (
  <section className="bg-background py-24 md:py-32" data-testid="problem-section">
    <div className="container mx-auto px-4 sm:px-6 lg:px-8">
      <Reveal className="max-w-3xl">
        <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold mb-4">The real problem</p>
        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
          The problem isn't a lack of content.
        </h2>
        <p className="mt-5 text-base sm:text-lg text-muted-foreground leading-relaxed">
          Students already have lectures, books, YouTube, PDFs, mock tests and coaching. The real problem
          is knowing what deserves their time right now.
        </p>
      </Reveal>

      <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        {PROBLEMS.map((p, i) => {
          const Icon = Icons[p.icon] || Icons.CircleAlert;
          return (
            <Reveal key={p.title} delay={i * 0.08}>
              <div className="h-full rounded-2xl border border-border bg-card p-6 hover:-translate-y-1 hover:shadow-lg transition-all duration-300">
                <div className="h-11 w-11 rounded-xl bg-destructive/10 flex items-center justify-center mb-5">
                  <Icon className="h-5 w-5 text-destructive" />
                </div>
                <h3 className="font-display text-base font-bold tracking-wide mb-2">{p.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{p.body}</p>
              </div>
            </Reveal>
          );
        })}
      </div>

      <Reveal delay={0.1} className="mt-16 text-center">
        <h3 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">
          sarkarijobai fixes the <span className="text-gradient">decision-making</span> problem.
        </h3>
      </Reveal>
    </div>
  </section>
);

export const TransformationSection = () => (
  <section className="bg-[#040406] text-white py-24 md:py-32" data-testid="transformation-section">
    <div className="container mx-auto px-4 sm:px-6 lg:px-8">
      <Reveal className="text-center max-w-2xl mx-auto mb-14">
        <p className="text-xs uppercase tracking-[0.2em] text-amber-300 font-semibold mb-4">The shift</p>
        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
          From constant guessing to deliberate preparation.
        </h2>
      </Reveal>

      <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
        <Reveal>
          <div className="h-full rounded-3xl border border-white/10 bg-white/[0.02] p-8">
            <div className="flex items-center gap-2 mb-6">
              <div className="h-8 w-8 rounded-lg bg-red-500/15 flex items-center justify-center">
                <X className="h-4 w-4 text-red-400" />
              </div>
              <h3 className="font-display text-sm font-bold tracking-wide text-white/70">WITHOUT RANKPILOT</h3>
            </div>
            <ul className="space-y-3.5">
              {WITHOUT.map((w) => (
                <li key={w} className="text-sm text-white/50 italic border-l-2 border-white/10 pl-4">"{w}"</li>
              ))}
            </ul>
          </div>
        </Reveal>

        <Reveal delay={0.12}>
          <div className="h-full rounded-3xl border border-amber-400/25 bg-gradient-to-b from-amber-500/[0.08] to-transparent p-8 glow-cyan">
            <div className="flex items-center gap-2 mb-6">
              <div className="h-8 w-8 rounded-lg bg-amber-400/20 flex items-center justify-center">
                <Check className="h-4 w-4 text-amber-300" />
              </div>
              <h3 className="font-display text-sm font-bold tracking-wide text-amber-300">WITH RANKPILOT</h3>
            </div>
            <ul className="space-y-3.5">
              {WITH.map((w) => (
                <li key={w} className="text-sm text-white/90 border-l-2 border-amber-400/40 pl-4">"{w}"</li>
              ))}
            </ul>
          </div>
        </Reveal>
      </div>

      <Reveal delay={0.1} className="mt-16 text-center">
        <h3 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">
          Less guessing. <span className="text-gradient">More deliberate preparation.</span>
        </h3>
      </Reveal>
    </div>
  </section>
);
